import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { putUserSettingsCloud } from "./api/client";
import { isCloudSessionActive } from "./cloud-session";
import { isApiConfigured } from "./api/config";
import {
  applyThemePreference,
  DEFAULT_USER_SETTINGS,
  loadUserSettings,
  normalizeDefaultCurrency,
  saveUserSettings,
  USER_SETTINGS_CLOUD_APPLIED_EVENT,
  type ThemePreference,
  type UserSettingsState,
} from "./user-settings";

type SettingsContextValue = {
  settings: UserSettingsState;
  setSettings: (next: UserSettingsState) => void;
  patchSettings: (partial: Partial<UserSettingsState>) => void;
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettingsState] = useState<UserSettingsState>(() =>
    loadUserSettings(),
  );

  useLayoutEffect(() => {
    applyThemePreference(settings.theme);
  }, [settings.theme]);

  useEffect(() => {
    if (settings.theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = () => applyThemePreference("system");
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, [settings.theme]);

  useEffect(() => {
    const onCloudApplied = (ev: Event) => {
      const ce = ev as CustomEvent<UserSettingsState>;
      if (ce.detail) setSettingsState(ce.detail);
    };
    window.addEventListener(USER_SETTINGS_CLOUD_APPLIED_EVENT, onCloudApplied);
    return () =>
      window.removeEventListener(USER_SETTINGS_CLOUD_APPLIED_EVENT, onCloudApplied);
  }, []);

  const mirrorSettingsToCloud = useCallback((merged: UserSettingsState) => {
    if (!isApiConfigured() || !isCloudSessionActive()) return;
    void putUserSettingsCloud(merged).catch((e) => {
      console.warn("Could not sync settings to your account", e);
    });
  }, []);

  const setSettings = useCallback((next: UserSettingsState) => {
    const normalized: UserSettingsState = {
      ...next,
      defaultCurrency: normalizeDefaultCurrency(next.defaultCurrency),
    };
    setSettingsState(normalized);
    saveUserSettings(normalized);
    mirrorSettingsToCloud(normalized);
  }, [mirrorSettingsToCloud]);

  const patchSettings = useCallback(
    (partial: Partial<UserSettingsState>) => {
      setSettingsState((prev) => {
        const merged: UserSettingsState = {
          ...prev,
          ...partial,
          defaultCurrency: normalizeDefaultCurrency(
            partial.defaultCurrency ?? prev.defaultCurrency,
          ),
        };
        saveUserSettings(merged);
        mirrorSettingsToCloud(merged);
        return merged;
      });
    },
    [mirrorSettingsToCloud],
  );

  const value = useMemo<SettingsContextValue>(
    () => ({ settings, setSettings, patchSettings }),
    [settings, setSettings, patchSettings],
  );

  return (
    <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
  );
}

export function useUserSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    throw new Error("useUserSettings must be used within SettingsProvider");
  }
  return ctx;
}

/** Safe when provider may be absent (e.g. tests) — returns defaults + noop setters. */
export function useUserSettingsOptional(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  return (
    ctx ?? {
      settings: DEFAULT_USER_SETTINGS,
      setSettings: () => {},
      patchSettings: () => {},
    }
  );
}

export type { ThemePreference, UserSettingsState };
